import React, { useState } from 'react';
import { 
  Box, Button, Container, Grid, Link, Paper, TextField, 
  Typography, Avatar, CircularProgress, Alert, Divider
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import GoogleIcon from '@mui/icons-material/Google';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useSupabaseAuth } from '../../hooks/useSupabaseAuth';
import { supabase } from '../../supabase/config';

const Signup = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const navigate = useNavigate();
  const { signUp, signInWithGoogleOAuth } = useSupabaseAuth();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const validateForm = () => {
    if (!formData.name || !formData.email || !formData.password || !formData.confirmPassword) {
      setError('All fields are required');
      return false;
    }
    
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!validateForm()) return;

    try {
      setLoading(true);
      
      // Create user with Supabase Auth
      const { data, error } = await signUp(
        formData.email,
        formData.password,
        { 
          metadata: {
            name: formData.name
          }
        }
      );
      
      if (error) throw error;
      
      // Create user profile in Supabase
      try {
        const { error: profileError } = await supabase
          .from('profiles')
          .insert([{
            id: data.user.id,
            name: formData.name,
            email: formData.email,
            preferences: {
              dietary: [],
              allergies: [],
              favorite_ingredients: []
            }
          }]);
        
        if (profileError) console.warn('Error creating profile:', profileError);
      } catch (profileError) {
        console.error('Error creating profile:', profileError);
      }
      
      toast.success('Account created successfully! Please check your email for verification.');
      navigate('/login');
    } catch (error) {
      console.error('Signup error:', error);
      
      // User-friendly error messages
      if (error.message.includes('already registered')) {
        setError('Email already in use. Please use a different email or log in.');
      } else if (error.message.includes('weak')) {
        setError('Password is too weak. Please use a stronger password.');
      } else {
        setError('Failed to create account. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleGoogleSignup = async () => {
    try {
      setGoogleLoading(true);
      setError('');
      
      const { data, error } = await signInWithGoogleOAuth();
      
      if (error) throw error;
      
      // For OAuth, user will be redirected away, so no need to navigate
      // We'll show a loading message for better UX
      toast.info('Redirecting to Google for authentication...');
    } catch (error) {
      console.error('Google signup error:', error);
      setError('Failed to sign up with Google. Please try again.');
    } finally {
      // Keep loading true as we're being redirected
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Paper
        elevation={3}
        sx={{
          marginTop: 8,
          padding: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          borderRadius: 2
        }}
      >
        <Avatar sx={{ margin: 1, bgcolor: 'primary.main' }}>
          <LockOutlinedIcon />
        </Avatar>
        <Typography component="h1" variant="h5">
          Sign Up
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ width: '100%', mt: 2 }}>
            {error}
          </Alert>
        )}
        
        <Box sx={{ width: '100%', mt: 2 }}>
          {/* Google Signup Button */}
          <Button
            fullWidth
            variant="outlined"
            startIcon={<GoogleIcon />}
            onClick={handleGoogleSignup}
            disabled={googleLoading}
            sx={{
              py: 1.2,
              mb: 2,
              borderColor: '#dadce0',
              color: 'text.primary',
              '&:hover': {
                bgcolor: 'rgba(66, 133, 244, 0.04)',
                borderColor: '#d2e3fc'
              }
            }}
          >
            {googleLoading ? <CircularProgress size={24} /> : 'Sign up with Google'}
          </Button>
          
          <Divider sx={{ my: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
              OR
            </Typography>
          </Divider>
        </Box>
        
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
          <TextField
            margin="normal"
            required
            fullWidth
            name="name"
            label="Full Name"
            value={formData.name}
            onChange={handleChange}
            autoFocus
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="email"
            label="Email Address"
            type="email"
            value={formData.email}
            onChange={handleChange}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="Password"
            type="password"
            value={formData.password}
            onChange={handleChange}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="confirmPassword"
            label="Confirm Password"
            type="password"
            value={formData.confirmPassword}
            onChange={handleChange}
          />
          
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{ mt: 3, mb: 2 }}
          >
            {loading ? <CircularProgress size={24} /> : 'Sign Up'}
          </Button>
          <Grid container justifyContent="flex-end">
            <Grid item>
              <Link component={RouterLink} to="/login" variant="body2">
                Already have an account? Sign in
              </Link>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Container>
  );
};

export default Signup;
